import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, COMPANY } from "@/data/company";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/80 border-b border-slate-200"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src={COMPANY.logo}
            alt="Finovest Holdings Ltd"
            className="w-14 h-14 lg:w-16 lg:h-16 object-contain"
          />
          <span className="font-serif text-base lg:text-lg tracking-[0.22em] text-slate-900">
            FINOVEST
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[13px] tracking-wide transition-colors ${
                  isActive
                    ? "text-slate-900 font-medium"
                    : "text-slate-500 hover:text-slate-900"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="navbar-cta"
            className="text-[12px] tracking-[0.18em] uppercase font-semibold bg-slate-900 text-white px-5 py-2.5 hover:bg-slate-800 transition-colors"
          >
            Get in touch
          </Link>
        </nav>

        <button
          type="button"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 -mr-2 text-slate-900"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-slate-200 bg-white"
        >
          <div className="px-6 py-4 flex flex-col gap-2">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-2 text-[14px] ${
                    isActive
                      ? "text-slate-900 font-medium"
                      : "text-slate-600"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta"
              className="mt-2 text-[12px] tracking-[0.18em] uppercase font-semibold bg-slate-900 text-white px-5 py-3 text-center"
            >
              Get in touch
            </Link>
            <div className="mt-2 text-[11px] text-slate-500 tracking-wide">
              {COMPANY.registration}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
